import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from './firebase';
import { useAdmin } from './hooks/useAdmin';
import { Link } from 'react-router-dom';
import './VisitorStatistics.css'; // reuse existing stats styles

export default function AdminUsers() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('lastSeen');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // ── 1. Collect all authenticated user activity from visitorLogs ──
      const logsSnap = await getDocs(
        query(collection(db, 'visitorLogs'), orderBy('timestamp', 'desc'), limit(20000))
      );
      const userMap = {}; // keyed by email

      logsSnap.forEach(d => {
        const v = d.data();
        if (!v.isAuthenticated || !v.userEmail) return;
        if (v.userEmail === 'giannisaliev@gmail.com') return; // exclude super-admin from list

        const key = v.userEmail;
        const ts = v.timestamp?.toDate?.() || new Date(v.timestamp);

        if (!userMap[key]) {
          userMap[key] = {
            email: key,
            name: v.userName || v.userEmail,
            userId: v.userId || null,
            firstSeen: ts,
            lastSeen: ts,
            visits: 0,
            interactions: 0,
            pages: new Set(),
            recentPages: [],
            shareCards: 0,
            trackPlays: 0,
            trackDownloads: 0,
            comparisons: 0,
            dancerClaims: 0,
            studioClaims: 0,
            isAdmin: false,
          };
        }

        const u = userMap[key];
        if (ts < u.firstSeen) u.firstSeen = ts;
        if (ts > u.lastSeen) { u.lastSeen = ts; u.name = v.userName || u.name; }

        if (v.type === 'interaction') {
          u.interactions++;
        } else {
          u.visits++;
          const page = v.page || v.path;
          if (page) {
            u.pages.add(page);
            if (u.recentPages.length < 5) u.recentPages.push(page);
          }
        }
      });

      // ── 2. Enrich with share card events ──
      const shareSnap = await getDocs(
        query(collection(db, 'shareCardEvents'), orderBy('timestamp', 'desc'), limit(5000))
      );
      shareSnap.forEach(d => {
        const e = d.data();
        const email = e.generatedBy?.email;
        if (!email || email === 'giannisaliev@gmail.com') return;
        if (!userMap[email]) {
          userMap[email] = {
            email,
            name: e.generatedBy?.name || email,
            userId: e.generatedBy?.userId || null,
            firstSeen: e.timestamp?.toDate?.() || new Date(e.timestamp),
            lastSeen: e.timestamp?.toDate?.() || new Date(e.timestamp),
            visits: 0, interactions: 0, pages: new Set(), recentPages: [],
            shareCards: 0, trackPlays: 0, trackDownloads: 0, comparisons: 0,
            dancerClaims: 0, studioClaims: 0, isAdmin: false,
          };
        }
        userMap[email].shareCards++;
      });

      // ── 3. Track play / download stats ──
      const trackSnap = await getDocs(
        query(collection(db, 'trackPlayStats'), orderBy('timestamp', 'desc'), limit(5000))
      );
      trackSnap.forEach(d => {
        const e = d.data();
        const email = e.userEmail;
        if (!email || email === 'giannisaliev@gmail.com') return;
        if (!userMap[email]) {
          userMap[email] = {
            email,
            name: e.userName || email,
            userId: e.userId || null,
            firstSeen: e.timestamp ? new Date(e.timestamp) : new Date(),
            lastSeen: e.timestamp ? new Date(e.timestamp) : new Date(),
            visits: 0, interactions: 0, pages: new Set(), recentPages: [],
            shareCards: 0, trackPlays: 0, trackDownloads: 0, comparisons: 0,
            dancerClaims: 0, studioClaims: 0, isAdmin: false,
          };
        }
        if (e.eventType === 'play') userMap[email].trackPlays++;
        else if (e.eventType === 'download') userMap[email].trackDownloads++;
      });

      // ── 4. Comparison events ──
      const compSnap = await getDocs(
        query(collection(db, 'dancerComparisons'), orderBy('timestamp', 'desc'), limit(5000))
      );
      compSnap.forEach(d => {
        const e = d.data();
        const email = e.userEmail;
        if (!email || email === 'giannisaliev@gmail.com') return;
        if (!userMap[email]) {
          userMap[email] = {
            email,
            name: e.userName || email,
            userId: e.userId || null,
            firstSeen: e.timestamp ? new Date(e.timestamp) : new Date(),
            lastSeen: e.timestamp ? new Date(e.timestamp) : new Date(),
            visits: 0, interactions: 0, pages: new Set(), recentPages: [],
            shareCards: 0, trackPlays: 0, trackDownloads: 0, comparisons: 0,
            dancerClaims: 0, studioClaims: 0, isAdmin: false,
          };
        }
        userMap[email].comparisons++;
      });

      // ── 5. Dancer & Studio claims ──
      const [claimsSnap, studioClaimsSnap] = await Promise.all([
        getDocs(collection(db, 'dancerClaims')),
        getDocs(collection(db, 'studioClaims')),
      ]);
      claimsSnap.forEach(d => {
        const e = d.data();
        const email = e.userEmail || e.claimantEmail;
        if (!email || email === 'giannisaliev@gmail.com') return;
        if (!userMap[email]) {
          userMap[email] = {
            email,
            name: e.userName || e.claimantName || email,
            userId: e.userId || null,
            firstSeen: new Date(), lastSeen: new Date(),
            visits: 0, interactions: 0, pages: new Set(), recentPages: [],
            shareCards: 0, trackPlays: 0, trackDownloads: 0, comparisons: 0,
            dancerClaims: 0, studioClaims: 0, isAdmin: false,
          };
        }
        userMap[email].dancerClaims++;
        if (e.claimantName && !userMap[email].name.includes('@')) {
          userMap[email].name = e.claimantName;
        }
      });
      studioClaimsSnap.forEach(d => {
        const e = d.data();
        const email = e.userEmail || e.claimantEmail;
        if (!email || email === 'giannisaliev@gmail.com') return;
        if (!userMap[email]) {
          userMap[email] = {
            email,
            name: e.userName || e.claimantName || email,
            userId: e.userId || null,
            firstSeen: new Date(), lastSeen: new Date(),
            visits: 0, interactions: 0, pages: new Set(), recentPages: [],
            shareCards: 0, trackPlays: 0, trackDownloads: 0, comparisons: 0,
            dancerClaims: 0, studioClaims: 0, isAdmin: false,
          };
        }
        userMap[email].studioClaims++;
      });

      // ── 6. Firestore users collection (isAdmin flag + any profile data) ──
      const usersSnap = await getDocs(collection(db, 'users'));
      const uidToEmail = {};
      usersSnap.forEach(d => {
        const data = d.data();
        // Match by userId if stored, or by email field
        const emailField = data.email;
        if (emailField && userMap[emailField]) {
          userMap[emailField].isAdmin = !!data.isAdmin;
          if (data.displayName) userMap[emailField].name = data.displayName;
        }
        if (data.isAdmin && emailField) {
          uidToEmail[d.id] = emailField;
        }
      });

      // Convert to array, convert Sets to counts
      const userList = Object.values(userMap).map(u => ({
        ...u,
        pageCount: u.pages.size,
        pages: undefined, // remove Set (not serialisable)
      }));

      setUsers(userList);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const fmt = (date) => {
    if (!date) return '—';
    try { return new Date(date).toLocaleDateString('el-GR'); }
    catch { return '—'; }
  };

  const activityScore = u =>
    u.visits + u.interactions * 2 + u.shareCards * 3 +
    u.trackPlays + u.trackDownloads * 2 + u.comparisons +
    u.dancerClaims * 5 + u.studioClaims * 5;

  const filtered = users
    .filter(u => {
      if (!search) return true;
      const q = search.toLowerCase();
      return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (sortField === 'activity') { va = activityScore(a); vb = activityScore(b); }
      if (sortField === 'lastSeen' || sortField === 'firstSeen') {
        va = va ? new Date(va).getTime() : 0;
        vb = vb ? new Date(vb).getTime() : 0;
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });

  const SortTh = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      title={`Sort by ${field}`}
    >
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </th>
  );

  if (adminLoading) return <div style={{ padding: '2rem' }}>Loading...</div>;
  if (!isAdmin) return <div style={{ padding: '2rem' }}>🔒 Access Denied</div>;

  const adminCount = users.filter(u => u.isAdmin).length;
  const activeCount = users.filter(u => activityScore(u) > 0).length;

  return (
    <div className="visitor-stats-container">
      {/* Header */}
      <div className="stats-header">
        <div className="stats-header-left">
          <Link to="/admin" className="back-to-admin">← Admin Dashboard</Link>
          <h1>👥 Registered Users</h1>
          <p className="stats-subtitle">All users who have interacted with the application</p>
        </div>
        <button className="refresh-btn" onClick={loadUsers} disabled={loading}>
          {loading ? '⏳ Loading…' : '🔄 Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="loading-message">Loading users…</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="stats-summary-grid">
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{users.length}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-label">Active Users</div>
                <div className="stat-value">{activeCount}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🛡️</div>
              <div className="stat-content">
                <div className="stat-label">Admins</div>
                <div className="stat-value">{adminCount}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏅</div>
              <div className="stat-content">
                <div className="stat-label">Dancer Claims</div>
                <div className="stat-value">{users.reduce((s, u) => s + u.dancerClaims, 0)}</div>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="stats-section" style={{ paddingBottom: '0.5rem' }}>
            <input
              type="text"
              placeholder="🔍 Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '0.6rem 1rem', borderRadius: '8px',
                border: '1px solid #ddd', fontSize: '0.95rem', boxSizing: 'border-box'
              }}
            />
          </div>

          {/* User Detail Panel */}
          {selectedUser && (
            <div className="stats-section" style={{ background: '#f0f4ff', border: '1px solid #c3d1f7' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <h3 style={{ margin: 0 }}>
                  {selectedUser.isAdmin ? '🛡️ ' : '👤 '}
                  {selectedUser.name}
                  {selectedUser.isAdmin && <span style={{ fontSize: '0.75rem', background: '#6c47ff', color: 'white', borderRadius: '4px', padding: '2px 6px', marginLeft: '0.5rem' }}>Admin</span>}
                </h3>
                <button
                  onClick={() => setSelectedUser(null)}
                  style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}
                >✕</button>
              </div>
              <p style={{ margin: '0.25rem 0 1rem', color: '#666', fontSize: '0.9rem' }}>{selectedUser.email}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                {[
                  { label: 'First Seen', value: fmt(selectedUser.firstSeen) },
                  { label: 'Last Seen', value: fmt(selectedUser.lastSeen) },
                  { label: 'Page Visits', value: selectedUser.visits },
                  { label: 'Unique Pages', value: selectedUser.pageCount },
                  { label: 'Interactions', value: selectedUser.interactions },
                  { label: 'Share Cards', value: selectedUser.shareCards },
                  { label: 'Track Plays', value: selectedUser.trackPlays },
                  { label: 'Downloads', value: selectedUser.trackDownloads },
                  { label: 'Comparisons', value: selectedUser.comparisons },
                  { label: 'Dancer Claims', value: selectedUser.dancerClaims },
                  { label: 'Studio Claims', value: selectedUser.studioClaims },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'white', borderRadius: '8px', padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>{label}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2c3e50' }}>{value}</div>
                  </div>
                ))}
              </div>
              {selectedUser.recentPages?.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.35rem' }}>Recently visited pages:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {selectedUser.recentPages.map((p, i) => (
                      <span key={i} style={{ background: '#e8eaf6', borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', color: '#3949ab' }}>{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Users Table */}
          <div className="stats-section">
            <h2>👥 All Users ({filtered.length})</h2>
            <div className="recent-visits-table-wrapper">
              <table className="recent-visits-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortTh field="name">Name</SortTh>
                    <SortTh field="email">Email</SortTh>
                    <th>Role</th>
                    <SortTh field="firstSeen">First Seen</SortTh>
                    <SortTh field="lastSeen">Last Seen</SortTh>
                    <SortTh field="visits">Visits</SortTh>
                    <SortTh field="interactions">Actions</SortTh>
                    <SortTh field="shareCards">Shares</SortTh>
                    <SortTh field="trackPlays">Plays</SortTh>
                    <SortTh field="comparisons">Compares</SortTh>
                    <SortTh field="dancerClaims">Claims</SortTh>
                    <SortTh field="activity">Activity ⭐</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => (
                    <tr
                      key={u.email}
                      onClick={() => setSelectedUser(selectedUser?.email === u.email ? null : u)}
                      style={{ cursor: 'pointer', background: selectedUser?.email === u.email ? '#eef2ff' : undefined }}
                    >
                      <td className="visit-time">{i + 1}</td>
                      <td className="visit-page" style={{ fontWeight: 600 }}>
                        {u.isAdmin && <span title="Admin" style={{ marginRight: '4px' }}>🛡️</span>}
                        {u.name}
                      </td>
                      <td className="visit-page" style={{ fontSize: '0.82rem', color: '#555' }}>{u.email}</td>
                      <td className="visit-device">
                        {u.isAdmin
                          ? <span style={{ background: '#6c47ff', color: 'white', borderRadius: '4px', padding: '1px 6px', fontSize: '0.75rem' }}>Admin</span>
                          : <span style={{ background: '#e8f5e9', color: '#388e3c', borderRadius: '4px', padding: '1px 6px', fontSize: '0.75rem' }}>User</span>
                        }
                      </td>
                      <td className="visit-time">{fmt(u.firstSeen)}</td>
                      <td className="visit-time">{fmt(u.lastSeen)}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>{u.visits || '—'}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>{u.interactions || '—'}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>{u.shareCards || '—'}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>{u.trackPlays || '—'}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>{u.comparisons || '—'}</td>
                      <td className="visit-device" style={{ textAlign: 'center' }}>
                        {(u.dancerClaims + u.studioClaims) || '—'}
                      </td>
                      <td className="visit-device" style={{ textAlign: 'center', fontWeight: 700 }}>
                        {activityScore(u) || '—'}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={13} style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
